<template>
  <Modal
    v-if="isEditorImageManagementModalShowing"
    @onCloseModal="onClose">
    <div class="modal-header">
      <h5>รูปภาพ</h5>
      <button
        @click="onClose"
        class="btn btn-link btm-sm btn-close-container">
        <i class="fas fa-times"></i>
      </button>
    </div>
    <div class="modal-body">
      <div class="alert alert-danger" v-if="isShowMessageError">
        <strong>มีข้อผิดพลาดเกิดขึ้น</strong>
        <ul class="error-container">
          <li
            v-for="(error, errorIndex) in fileValidError"
            :key="errorIndex">{{error}}</li>
          <li
            v-for="(uploadError, uploadErrorIndex) in uploadImageStateError.data"
            :key="uploadErrorIndex">{{uploadError}}</li>
        </ul>
      </div>
      <Tab>
        <div class="tab-item"
          v-for="(tabItem, tabIndex) in tabMenu"
          :key="tabIndex"
          :class="{'active': selectedTabIndex === tabIndex }"
          @click.stop="selectedTab(tabIndex)">
          {{tabItem.value}}
        </div>
      </Tab>
      <div class="tab-item-container tab-item-general"
        v-if="selectedTabIndex === 0">
        <div class="upload-file-container"
          :class="{'browse-file': !previewImage}">
          <!-- select Image -->
          <EmptyMessage v-if="!previewImage">
            <input
              @change="fileBrowsed"
              type="file"
              ref="file"
              class="input-file"
              accept="image/x-png, image/gif,image/jpeg">
            <span slot="icon" class="icon">
              <i class="fas fa-cloud-upload-alt"></i>
            </span>
            <div class="title">ลากไฟล์หรือคลิกเพื่ออัปโหลด</div>
          </EmptyMessage>
          <div
            v-if="previewImage"
            class="image-container">
            <div class="action">
              <button
                type="button"
                class="btn btn-light btn-action"
                @click.stop="onRemovePreview">
                <i class="far fa-trash-alt"></i>
              </button>
            </div>
            <ImageView
              :source="previewImage"
              :imageSize="imageViewSize" />
          </div>
        </div>
      </div>
      <div class="tab-item-container tab-item-general" v-if="selectedTabIndex === 1">
        <div class="image-options-container mb-1">
          <input class="form-control" v-model="previewImage" placeholder="วางหรือพิมพ์ URL" />
        </div>
        <div class="upload-file-container" v-if="previewImage">
          <!-- select Image -->
          <div
            class="image-container">
            <div class="action">
              <button
                type="button"
                class="btn btn-light btn-action"
                @click.stop="onRemovePreview">
                <i class="far fa-trash-alt"></i>
              </button>
            </div>
            <ImageView
              :source="previewImage"
              :imageSize="imageViewSize" />
          </div>
        </div>
      </div>
    </div>
    <div class="modal-footer">
      <button
        type="button"
        class="btn btn-link btn-remove"
        @click.stop="onRemovePreview"
        v-if="previewImage">
        <i class="fas fa-angle-left icon"></i> เลือกใหม่
      </button>
      <button
        type="submit"
        class="btn btn-primary"
        @click.stop="onConfirm"
        :disabled="!isValid">
        แนบรูป
      </button>
    </div>
  </Modal>
</template>

<script>
import _ from 'lodash';
import Modal from '@/components/Modal.vue';
import EmptyMessage from '@/components/EmptyMessage.vue';
import Tab from '@/components/Tab.vue';
import { uploadImage } from '@/image/api';
import ImageView from '@/image/ImageView.vue';

export default {
  props: {
    onSubmit: {
      type: Function,
      default: () => {},
    },
  },
  components: {
    Tab,
    Modal,
    EmptyMessage,
    ImageView,
  },
  data() {
    return {
      isEditorImageManagementModalShowing: false,
      imageURL: null,
      command: null,
      selectedTabIndex: 0,
      previewImage: null,
      uploadImageStateError: [],
      fileValidError: [],
      maxFileSize: 20 * 1024 * 2014,
      isUploadingImage: false,
      imageViewSize: 'content640w',
      imageType: 'generalCoverImage',
      tabMenu: [
        {
          key: 'upload',
          value: 'อัปโหลดจากเครื่อง',
        },
        {
          key: 'information',
          value: 'เพิ่มไฟล์รูปด้วย URL',
        },
      ],
    };
  },
  computed: {
    isValid() {
      if (!this.previewImage) {
        return false;
      }

      return _.isEmpty(this.fileValidError) && _.isEmpty(this.uploadImageStateError);
    },
    isShowMessageError() {
      return !_.isEmpty(this.fileValidError) || !_.isEmpty(this.uploadImageStateError);
    },
    computedImageURL() {
      if (this.previewImage && typeof this.previewImage === 'object') {
        return _.get(this.previewImage, 'urls.content640w', null);
      }
      return this.previewImage;
    },
  },
  methods: {
    onClose() {
      this.isEditorImageManagementModalShowing = false;
    },
    onShowEditorImageManagementModal(command) {
      this.command = command;
      this.isEditorImageManagementModalShowing = !this.isEditorImageManagementModalShowing;
    },
    onCloseEditorImageManagementModal() {
      this.isEditorImageManagementModalShowing = false;
    },
    onConfirm() {
      // Some check can be done here, like if `youtubeID` is not false.
      const data = {
        command: this.command,
        data: {
          src: this.computedImageURL,
        },
      };
      this.$emit('onSubmit', data);
      this.onCloseEditorImageManagementModal();
    },
    getErrorCode() {
      return _.get(this.uploadImageStateError, 'status', null);
    },
    async uploadFile(fileImage) {
      this.isUploadingImage = true;
      try {
        const response = await uploadImage(this.imageType, fileImage);
        this.previewImage = response.data;
        this.isUploadingImage = false;
        this.uploadImageStateError = null;
      } catch (error) {
        this.isUploadingImage = false;
        this.uploadImageStateError = error.response.data;
      }
    },
    onRemovePreview() {
      this.previewImage = null;
    },
    isValidFile(file) {
      this.fileValidError = [];

      if (!file.type.match('image.*')) {
        this.fileValidError.push(`ไฟล์ ${file.name} ไม่ใช่ไฟล์ภาพ (.jpg, .jpeg, .gif, .png)`);
        return false;
      }
      if (file.size > this.maxFileSize) {
        this.fileValidError.push(`ไฟล์ ${file.name} มีขนาดใหญ่กว่า 20 MB`);
        return false;
      }

      return true;
    },
    fileBrowsed() {
      const fileImage = this.$refs.file.files[0];
      if (this.isValidFile(fileImage)) {
        this.uploadFile(fileImage);
      }
    },
    selectedTab(selectedTabIndex) {
      // reset preview image
      if (this.selectedTabIndex !== 0 && this.previewImage) {
        this.previewImage = null;
      }
      this.selectedTabIndex = selectedTabIndex;
    },
    selectImage(imageObject) {
      this.previewImage = imageObject;
    },
    isSelectedImage(image) {
      return _.isEqual(this.previewImage, image);
    },
  },
};
</script>

<style lang="scss" scoped>
@import './src/assets/scss/variables.scss';


.tab-item-container {
  min-height: 300px;
  max-height: 50vh;
}

.upload-file-container {
  position: relative;

  &.browse-file {
    border: 2px dashed $gray-200;
    cursor: pointer;
    transition: all 0.2s ease-out;

    &:hover {
      background-color: $gray-100;
    }
  }
}

.input-file {
  opacity: 0;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  z-index: 100;
  cursor: pointer;
}

.error-container {
  margin-bottom: 0;
  margin-top: $spacer * 0.5;
  padding-left: $spacer;
}

.image-container {
  background-color: $gray-700;
  position: relative;

  .action {
    position: absolute;
    top: $spacer * 0.5;
    right: 0;
    z-index: 101;
  }
}

.image-options-container {
  display: flex;
  flex-wrap: wrap;

  .image-item-container {
    flex: 0 0 calc(33.33% - 12px);
    height: auto;
    margin-right: $spacer;
    margin-bottom: $spacer;
    cursor: pointer;
    position: relative;
    border: 2px solid transparent;

    .image-item-overlay {
      opacity: 0;
      background-color: rgba(0, 0, 0, 0.5);

      display: flex;
      align-items: center;
      justify-content: center;
      color: white;

      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;

      z-index: 102;

      transition: all 0.2s ease-out;
    }

    &.is-selected {
      border: 2px solid $primary;
    }

    &:nth-child(3n+0) {
      margin-right: 0;
    }

    &:hover {
      .image-item-overlay {
        opacity: 1;
      }
    }
  }
}

</style>
